import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import {
  Calendar,
  Check,
  X,
  RefreshCw,
  ExternalLink,
  Unlink,
  CalendarPlus,
  Loader2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CalendarSettings = () => {
  const { token, user } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [calendarStatus, setCalendarStatus] = useState({ connected: false, enabled: false });
  const [upcomingEvents, setUpcomingEvents] = useState([]);

  useEffect(() => {
    // Check for OAuth callback status
    const connected = searchParams.get('connected');
    const error = searchParams.get('error');
    
    if (connected === 'true') {
      toast.success('Google Calendar connecté avec succès !');
      // Clean URL
      navigate('/settings/calendar', { replace: true });
    } else if (error) {
      toast.error('Erreur de connexion à Google Calendar');
      navigate('/settings/calendar', { replace: true });
    }
  }, [searchParams, navigate]);

  useEffect(() => {
    fetchCalendarStatus();
  }, [token]);

  const fetchCalendarStatus = async () => {
    if (!token) return;
    
    try {
      const res = await fetch(`${API_URL}/api/calendar/google/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setCalendarStatus(data);
        
        if (data.connected) {
          fetchUpcomingEvents();
        }
      }
    } catch (error) {
      console.error('Failed to fetch calendar status:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUpcomingEvents = async () => {
    try {
      const res = await fetch(`${API_URL}/api/calendar/google/events?max_results=5`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setUpcomingEvents(data.events || []);
      }
    } catch (error) {
      console.error('Failed to fetch events:', error);
    }
  };

  const handleConnect = async () => {
    setConnecting(true);
    
    try {
      const res = await fetch(`${API_URL}/api/calendar/google/connect`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        // Redirect to Google OAuth
        window.location.href = data.authorization_url;
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Erreur de connexion');
        setConnecting(false);
      }
    } catch (error) {
      toast.error('Erreur de connexion');
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!confirm('Voulez-vous vraiment déconnecter Google Calendar ?')) return;
    
    setDisconnecting(true);
    
    try {
      const res = await fetch(`${API_URL}/api/calendar/google/disconnect`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success('Google Calendar déconnecté');
        setCalendarStatus({ ...calendarStatus, connected: false });
        setUpcomingEvents([]);
      } else {
        toast.error('Erreur lors de la déconnexion');
      }
    } catch (error) {
      toast.error('Erreur lors de la déconnexion');
    } finally {
      setDisconnecting(false);
    }
  };

  if (user?.role !== 'professional') {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 py-8">
        <div className="container mx-auto px-4 max-w-2xl">
          <Card>
            <CardContent className="py-12 text-center">
              <Calendar className="w-16 h-16 mx-auto text-slate-300 mb-4" />
              <h2 className="text-xl font-semibold text-slate-700 dark:text-slate-300 mb-2">
                Fonctionnalité réservée aux professionnels
              </h2>
              <p className="text-slate-500">
                Seuls les professionnels de santé peuvent synchroniser leur agenda.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 py-8">
      <div className="container mx-auto px-4 max-w-2xl">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white mb-2">
            Synchronisation Calendrier
          </h1>
          <p className="text-slate-600 dark:text-slate-400">
            Connectez votre Google Calendar pour synchroniser vos rendez-vous
          </p>
        </div>

        {/* Connection Status Card */}
        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`p-3 rounded-xl ${calendarStatus.connected ? 'bg-emerald-100 dark:bg-emerald-900/30' : 'bg-slate-100 dark:bg-slate-800'}`}>
                  <Calendar className={`w-6 h-6 ${calendarStatus.connected ? 'text-emerald-600' : 'text-slate-500'}`} />
                </div>
                <div>
                  <CardTitle className="text-lg">Google Calendar</CardTitle>
                  <CardDescription>
                    {calendarStatus.connected 
                      ? 'Votre agenda est synchronisé' 
                      : 'Non connecté'}
                  </CardDescription>
                </div>
              </div>
              <Badge variant={calendarStatus.connected ? 'default' : 'secondary'} className={calendarStatus.connected ? 'bg-emerald-500' : ''}>
                {calendarStatus.connected ? (
                  <><Check className="w-3 h-3 mr-1" /> Connecté</>
                ) : (
                  <><X className="w-3 h-3 mr-1" /> Non connecté</>
                )}
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
              </div>
            ) : calendarStatus.connected ? (
              <div className="space-y-4">
                <div className="bg-emerald-50 dark:bg-emerald-900/20 rounded-lg p-4">
                  <p className="text-emerald-700 dark:text-emerald-400 text-sm">
                    ✅ Vos nouveaux rendez-vous seront automatiquement ajoutés à votre Google Calendar.
                  </p>
                </div>
                
                <div className="flex gap-3">
                  <Button 
                    variant="outline" 
                    onClick={fetchUpcomingEvents}
                    className="gap-2"
                  >
                    <RefreshCw className="w-4 h-4" />
                    Actualiser
                  </Button>
                  <Button 
                    variant="outline" 
                    className="gap-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                    onClick={handleDisconnect}
                    disabled={disconnecting}
                  >
                    {disconnecting ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Unlink className="w-4 h-4" />
                    )}
                    Déconnecter
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
                  <h4 className="font-medium text-blue-900 dark:text-blue-300 mb-2">
                    Avantages de la synchronisation :
                  </h4>
                  <ul className="text-sm text-blue-700 dark:text-blue-400 space-y-1">
                    <li>• Vos RDV drbooking apparaissent dans Google Calendar</li>
                    <li>• Rappels automatiques via Google</li>
                    <li>• Vue unifiée de tous vos rendez-vous</li>
                  </ul>
                </div>
                
                <Button 
                  onClick={handleConnect}
                  disabled={connecting || !calendarStatus.enabled}
                  className="gap-2 bg-blue-600 hover:bg-blue-700"
                >
                  {connecting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <CalendarPlus className="w-4 h-4" />
                  )}
                  Connecter Google Calendar
                </Button>
                
                {!calendarStatus.enabled && (
                  <p className="text-amber-600 text-sm">
                    ⚠️ Le service Google Calendar n'est pas encore configuré.
                  </p>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Upcoming Events */}
        {calendarStatus.connected && upcomingEvents.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Prochains événements</CardTitle>
              <CardDescription>Vos 5 prochains événements Google Calendar</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {upcomingEvents.map((event, index) => (
                  <div 
                    key={event.id || index}
                    className="flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-800 rounded-lg"
                  >
                    <div className="w-2 h-2 rounded-full bg-blue-500" />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-900 dark:text-white truncate">
                        {event.summary || 'Sans titre'}
                      </p>
                      <p className="text-sm text-slate-500">
                        {event.start?.dateTime 
                          ? new Date(event.start.dateTime).toLocaleString('fr-FR', {
                              dateStyle: 'medium',
                              timeStyle: 'short'
                            })
                          : event.start?.date || 'Date non définie'}
                      </p>
                    </div>
                    {event.htmlLink && (
                      <a 
                        href={event.htmlLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-500 hover:text-blue-600"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default CalendarSettings;
